import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/lib/api';

/**
 * API Response type for standardized backend responses
 */
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Hook state for API calls
 */
interface UseApiState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  isStale: boolean;
}

/**
 * Hook return type
 */
interface UseApiReturn<T> extends UseApiState<T> {
  refetch: () => Promise<void>;
  setData: (data: T | null) => void;
}

/**
 * Options for the useApi hook
 */
interface UseApiOptions {
  /** Auto-fetch on mount */
  immediate?: boolean;
  /** Polling interval in ms (0 = disabled) */
  pollInterval?: number;
  /** Transform the response data */
  transform?: (data: unknown) => unknown;
}

/**
 * Custom hook for API data fetching with loading states
 * Provides smooth loading animations and error handling
 */
export function useApi<T>(
  endpoint: string,
  options: UseApiOptions = {}
): UseApiReturn<T> {
  const { immediate = true, pollInterval = 0, transform } = options;

  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    isLoading: immediate,
    error: null,
    isStale: false,
  });

  const fetchData = useCallback(async () => {
    try {
      // Mark as loading but keep old data (for smooth transitions)
      setState((prev) => ({ ...prev, isLoading: true, isStale: !!prev.data }));

      const response = await apiClient.get<T>(endpoint);
      const finalData = transform ? (transform(response) as T) : response;

      setState({
        data: finalData,
        isLoading: false,
        error: null,
        isStale: false,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
        isStale: false,
      }));
    }
  }, [endpoint, transform]);

  // Initial fetch
  useEffect(() => {
    if (immediate) {
      fetchData();
    }
  }, [immediate, fetchData]);

  // Polling
  useEffect(() => {
    if (pollInterval > 0) {
      const interval = setInterval(fetchData, pollInterval);
      return () => clearInterval(interval);
    }
  }, [pollInterval, fetchData]);

  const setData = useCallback((data: T | null) => {
    setState((prev) => ({ ...prev, data }));
  }, []);

  return {
    ...state,
    refetch: fetchData,
    setData,
  };
}

/**
 * Custom hook for POST/PUT/PATCH/DELETE operations
 */
interface UseMutationOptions<T> {
  onSuccess?: (data: T) => void;
  onError?: (error: string) => void;
}

interface UseMutationReturn<T, V> {
  mutate: (data?: V) => Promise<T | null>;
  isLoading: boolean;
  error: string | null;
  data: T | null;
  reset: () => void;
}

export function useMutation<T, V = unknown>(
  endpoint: string,
  method: 'post' | 'put' | 'patch' | 'delete' = 'post',
  options: UseMutationOptions<T> = {}
): UseMutationReturn<T, V> {
  const [state, setState] = useState({
    data: null as T | null,
    isLoading: false,
    error: null as string | null,
  });

  const mutate = useCallback(
    async (payload?: V): Promise<T | null> => {
      setState({ data: null, isLoading: true, error: null });

      try {
        let response: T;
        switch (method) {
          case 'post':
            response = await apiClient.post<T>(endpoint, payload);
            break;
          case 'put':
            response = await apiClient.put<T>(endpoint, payload);
            break;
          case 'patch':
            response = await apiClient.patch<T>(endpoint, payload);
            break;
          case 'delete':
            response = await apiClient.delete<T>(endpoint);
            break;
        }

        setState({ data: response, isLoading: false, error: null });
        options.onSuccess?.(response);
        return response;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Operation failed';
        setState({ data: null, isLoading: false, error: errorMessage });
        options.onError?.(errorMessage);
        return null;
      }
    },
    [endpoint, method, options]
  );

  const reset = useCallback(() => {
    setState({ data: null, isLoading: false, error: null });
  }, []);

  return { ...state, mutate, reset };
}

export default useApi;
